import * as React from 'react';
import { Check, Copy, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useDesignSystemLabels } from './labels';

/**
 * CopyableId — an identifier of record (hardware ID, digest, fingerprint,
 * MAC, release ID) shown in mono with a copy control.
 *
 * Truncation never hides which value it is: `truncate="middle"` keeps the
 * head and the tail (the parts people compare against a label or a log), and
 * the full value is always available to assistive technology, as the
 * element's tooltip, and on the clipboard.
 *
 * The copy strings default to the app's `DesignSystemLabelsProvider`; pass
 * props only to override them.
 */

type CopyState = 'idle' | 'copied' | 'failed';

export interface CopyableIdProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  value: string;
  /** What the value is, e.g. "Credential fingerprint". Names the copy button. */
  label?: string;
  truncate?: 'none' | 'end' | 'middle';
  /** Characters kept at the start when `truncate="middle"`. */
  head?: number;
  /** Characters kept at the end when `truncate="middle"`. */
  tail?: number;
  copyLabel?: string;
  copiedLabel?: string;
  copyFailedLabel?: string;
  /** Hide the copy control (the value stays selectable). */
  disableCopy?: boolean;
  onCopied?: (value: string) => void;
  size?: 'sm' | 'md';
}

const ELLIPSIS = '…';

function middleTruncate(value: string, head: number, tail: number): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}${ELLIPSIS}${value.slice(value.length - tail)}`;
}

async function writeClipboard(value: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  throw new Error('clipboard unavailable');
}

const RESET_AFTER_MS = 1600;

const CopyableId = React.forwardRef<HTMLSpanElement, CopyableIdProps>(
  (
    {
      value,
      label,
      truncate = 'none',
      head = 10,
      tail = 8,
      copyLabel: copyLabelProp,
      copiedLabel: copiedLabelProp,
      copyFailedLabel: copyFailedLabelProp,
      disableCopy = false,
      onCopied,
      size = 'sm',
      className,
      ...props
    },
    ref,
  ) => {
    const labels = useDesignSystemLabels();
    const copyLabel = copyLabelProp ?? labels.copy;
    const copiedLabel = copiedLabelProp ?? labels.copied;
    const copyFailedLabel = copyFailedLabelProp ?? labels.copyFailed;
    const [state, setState] = React.useState<CopyState>('idle');
    const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    React.useEffect(() => () => clearTimeout(timer.current), []);

    const shown = truncate === 'middle' ? middleTruncate(value, head, tail) : value;
    const buttonName = label ? `${copyLabel} ${label}` : copyLabel;

    const onCopy = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      event.stopPropagation();
      try {
        await writeClipboard(value);
        setState('copied');
        onCopied?.(value);
      } catch {
        setState('failed');
      }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setState('idle'), RESET_AFTER_MS);
    };

    return (
      <span
        ref={ref}
        data-slot="copyable-id"
        className={cn(
          'inline-flex max-w-full min-w-0 items-center gap-1 align-middle font-mono tabular-nums text-[var(--color-foreground)]',
          size === 'sm' ? 'text-xs' : 'text-[0.8125rem]',
          className,
        )}
        {...props}
      >
        <span
          title={value}
          aria-hidden={shown !== value ? true : undefined}
          className={cn(
            'min-w-0 select-all',
            truncate === 'end' && 'truncate',
            truncate === 'middle' && 'whitespace-nowrap',
            truncate === 'none' && 'break-all',
          )}
        >
          {shown}
        </span>
        {shown !== value ? <span className="sr-only">{value}</span> : null}
        {disableCopy ? null : (
          <button
            type="button"
            onClick={(event) => void onCopy(event)}
            aria-label={buttonName}
            title={buttonName}
            className={cn(
              'inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-[var(--color-foreground-3)] transition-colors',
              'hover:bg-[var(--color-surface-3)] hover:text-[var(--color-foreground)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
              state === 'copied' && 'text-[var(--color-success)]',
              state === 'failed' && 'text-[var(--color-danger)]',
            )}
          >
            {state === 'copied' ? (
              <Check aria-hidden className="size-3.5" />
            ) : state === 'failed' ? (
              <X aria-hidden className="size-3.5" />
            ) : (
              <Copy aria-hidden className="size-3.5" />
            )}
          </button>
        )}
        <span role="status" aria-live="polite" className="sr-only">
          {state === 'copied' ? copiedLabel : state === 'failed' ? copyFailedLabel : ''}
        </span>
      </span>
    );
  },
);
CopyableId.displayName = 'CopyableId';

export { CopyableId, middleTruncate };
